import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import {
  School,
  People,
  Assessment,
  Security,
  CloudSync,
  Support,
  Menu,
  Phone,
  Email,
  LocationOn,
  Facebook,
  Twitter,
  LinkedIn,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const features = [
    {
      icon: <People />,
      title: 'Student Management',
      description: 'Comprehensive student information system with enrollment, attendance, and performance tracking.',
    },
    {
      icon: <School />,
      title: 'Academic Management',
      description: 'Manage classes, subjects, exams, and academic calendar with ease.',
    },
    {
      icon: <Assessment />,
      title: 'Performance Analytics',
      description: 'Detailed reports and analytics for student performance and institutional insights.',
    },
    {
      icon: <Security />,
      title: 'Secure & Reliable',
      description: 'Bank-grade security with role-based access control and data protection.',
    },
    {
      icon: <CloudSync />,
      title: 'Cloud-Based',
      description: 'Access your data anywhere, anytime with our cloud-based infrastructure.',
    },
    {
      icon: <Support />,
      title: '24/7 Support',
      description: 'Round-the-clock technical support to ensure smooth operations.',
    },
  ];

  const testimonials = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'Principal, Greenwood High School',
      avatar: '/api/placeholder/60/60',
      rating: 5,
      comment: 'EMIS has revolutionized how we manage our school. The comprehensive features and user-friendly interface make administration effortless.',
    },
    {
      name: 'Michael Chen',
      role: 'IT Director, Metro Education District',
      avatar: '/api/placeholder/60/60',
      rating: 5,
      comment: 'The security features and scalability of EMIS impressed us. Perfect solution for managing multiple schools in our district.',
    },
    {
      name: 'Lisa Rodriguez',
      role: 'Teacher, Riverside Academy',
      avatar: '/api/placeholder/60/60',
      rating: 5,
      comment: 'As a teacher, I love how easy it is to track student progress and communicate with parents through EMIS.',
    },
  ];

  return (
    <Box>
      {/* Navigation */}
      <AppBar position="fixed" sx={{ backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <Box display="flex" alignItems="center" flexGrow={1}>
            <School sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
              EMIS
            </Typography>
          </Box>
          
          {!isMobile && (
            <Box display="flex" gap={3}>
              <Button color="primary" href="#home">Home</Button>
              <Button color="primary" href="#about">About</Button>
              <Button color="primary" href="#features">Features</Button>
              <Button color="primary" href="#contact">Contact</Button>
              <Button color="primary" onClick={() => navigate('/admission')}>Admission</Button>
              <Button color="primary" onClick={() => navigate('/track-application')}>Track Application</Button>
              <Button variant="contained" onClick={() => navigate('/login')}>Login</Button>
            </Box>
          )}
          
          {isMobile && (
            <IconButton color="primary">
              <Menu />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        id="home"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 'bold', mb: 2 }}>
                  Transform Your
                  <Box component="span" sx={{ color: '#FFD700', display: 'block' }}>
                    Education Management
                  </Box>
                </Typography>
                
                <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6 }}>
                  Comprehensive school management system designed to streamline operations, 
                  enhance communication, and improve educational outcomes.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#333',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      '&:hover': { backgroundColor: '#FFC107' },
                    }}
                    onClick={() => navigate('/admission')}
                  >
                    Apply for Admission
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      '&:hover': { borderColor: '#FFD700', backgroundColor: 'rgba(255,215,0,0.1)' },
                    }}
                    onClick={() => navigate('/track-application')}
                  >
                    Track Application
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      '&:hover': { borderColor: '#FFD700', backgroundColor: 'rgba(255,215,0,0.1)' },
                    }}
                    onClick={() => navigate('/login')}
                  >
                    Login to Portal
                  </Button>
                </Box>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    textAlign: 'center',
                  }}
                >
                  <img
                    src="/images/hero-dashboard.jpg"
                    alt="Education Management Dashboard"
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 'bold' }}>
              Powerful Features
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              Everything you need to manage your educational institution effectively
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                  About EMIS
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                  Education Management Information System (EMIS) is a comprehensive solution designed 
                  to digitize and streamline educational institution operations. From student enrollment 
                  to graduation, we provide tools that enhance efficiency and improve educational outcomes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}>
                  Our platform serves schools, colleges, and universities worldwide, helping them manage 
                  student data, academic records, attendance, examinations, and much more with ease and security.
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip label="10,000+ Students" color="primary" variant="outlined" />
                  <Chip label="500+ Schools" color="primary" variant="outlined" />
                  <Chip label="50+ Countries" color="primary" variant="outlined" />
                  <Chip label="99.9% Uptime" color="primary" variant="outlined" />
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <img
                  src="/images/about-education.jpg"
                  alt="About EMIS"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 'bold' }}>
              What Our Users Say
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              Trusted by educational institutions worldwide
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ height: '100%', p: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar src={testimonial.avatar} sx={{ mr: 2 }} />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" mb={2}>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} sx={{ color: '#FFD700', fontSize: '1.2rem' }} />
                        ))}
                      </Box>
                      <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                        "{testimonial.comment}"
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 'bold' }}>
              Get in Touch
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              Ready to transform your educational institution?
            </Typography>
          </motion.div>

          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                <Phone sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Phone
                </Typography>
                <Typography color="text.secondary">
                  +1 (555) 123-4567
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                <Email sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Email
                </Typography>
                <Typography color="text.secondary">
                  info@emis.edu
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                <LocationOn sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Address
                </Typography>
                <Typography color="text.secondary">
                  123 Education Street<br />
                  Learning City, LC 12345
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: '#333', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" mb={2}>
                <School sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  EMIS
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                Transforming education through innovative technology solutions.
              </Typography>
              <Box display="flex" gap={1}>
                <IconButton sx={{ color: 'white' }}>
                  <Facebook />
                </IconButton>
                <IconButton sx={{ color: 'white' }}>
                  <Twitter />
                </IconButton>
                <IconButton sx={{ color: 'white' }}>
                  <LinkedIn />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={4}>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Product
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Features
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Pricing
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Demo
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Support
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Help Center
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Documentation
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Contact
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Company
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      About
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Careers
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Blog
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Legal
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Privacy
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Terms
                    </Button>
                    <Button color="inherit" sx={{ justifyContent: 'flex-start', p: 0 }}>
                      Security
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 4, pt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © 2024 EMIS. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
